// util
import { isTeamCalendarSyncEvent } from "@clockwise/client-commons/src/util/event";
import { useIsOutlook } from "@clockwise/web-commons/src/util/OutlookContext";
// other components
import * as ISchema from "#webapp/src/__schema__";
import { AutopilotFormSaveOnEdit } from "#webapp/src/components/autopilot-form";
import { EventSummaryColorCategory } from "#webapp/src/components/event-summary-color-category";
import { H1 } from "#webapp/src/components/ui";
import { queryParams } from "#webapp/src/constants/site.constant";
import { EFlex } from "#webapp/src/util/event-tag.util";
import { ZonedMoment } from "@clockwise/web-commons/src/util/time-zone.util";
import * as React from "react";
// event-summary
import { formatEventTitle } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/formatEventTitle";
import { EventSummary as s } from "./EventSummary.styles";
import { EventTimeAndRecurrence } from "./EventTimeAndRecurrence";

export interface Props {
  attendeeNotInDomain?: boolean;
  calendarId: string;
  canAutopilot?: boolean;
  event: ISchema.IEvent;
  eventParent?: ISchema.IEventParent;
  hideColorSettings?: boolean;
  isAllDayEvent?: boolean;
  onClick?: () => void;
  open: boolean;
  org: ISchema.IOrg;
  organizerNotClockwiseUser?: boolean;
  originEvent?: ISchema.IEvent;
  user: ISchema.IUser;
  refetchEvent: () => void;
}

export const EventSummary = ({
  attendeeNotInDomain,
  calendarId,
  canAutopilot,
  event,
  eventParent,
  hideColorSettings,
  isAllDayEvent,
  onClick,
  org,
  organizerNotClockwiseUser,
  originEvent,
  user,
  refetchEvent,
}: Props) => {
  const isClickable = !!onClick;
  const containerStyle = isClickable ? s.containerWithClick : undefined;
  const isAllDaySameStartAndEndTime =
    event.isAllDay && event.startTime.millis === event.endTime.millis;
  const startTime = isAllDaySameStartAndEndTime
    ? new ZonedMoment(event.startTime.dateOrDateTime)
    : event.startTime && new ZonedMoment(event.startTime.millis);
  const endTime = isAllDaySameStartAndEndTime
    ? new ZonedMoment(event.endTime.dateOrDateTime)
    : event.endTime && new ZonedMoment(event.endTime.millis);
  let title = formatEventTitle(event.title, event.locked);

  // For personal events, pull the title off the originEvent if available
  if (originEvent && originEvent.title) {
    title = formatEventTitle(originEvent.title, originEvent.locked);
  }

  if (event.scrubbedEvent) {
    title = "Event";
  } else if (event.locked) {
    title = "Private Event";
  }

  let viaClockwise = false;
  const VIA_CLOCKWISE = "(via Clockwise)";
  if (title.endsWith(VIA_CLOCKWISE)) {
    title = title.substring(0, title.length - VIA_CLOCKWISE.length);
    viaClockwise = true;
  }

  const showTeamName = isTeamCalendarSyncEvent(event);

  // should autopilot be enabled on mount
  const searchParams = new URLSearchParams(window.location.search);
  const apEnableOnMount = searchParams.has(queryParams.apEnable);
  const apFlexDayOnMount = searchParams.has(queryParams.apFlexDay);
  const apFlexWeekOnMount = searchParams.has(queryParams.apFlexWeek);
  const apRoomFlexOnMount = searchParams.has(queryParams.apFlexRoom);

  const canViewColorCategory =
    event.eventPermissions !== ISchema.EventPermissions.NONE &&
    !showTeamName &&
    !hideColorSettings &&
    !event.scrubbedEvent &&
    !event.locked;

  const onClickContainer = () => {
    !!onClick && onClick();
  };

  const isOutlook = useIsOutlook();
  return (
    <div style={containerStyle} onClick={() => onClickContainer()}>
      {!isOutlook && (
        <>
          <H1
            style={{
              ...s.h2,
              paddingBottom: 0,
            }}
          >
            {title}
            {viaClockwise && (
              <span>
                <br />
                (via Clockwise)
              </span>
            )}
          </H1>
          <EventTimeAndRecurrence
            org={org}
            event={event}
            startTime={startTime}
            endTime={endTime}
            isAllDay={event.isAllDay}
            calendarId={calendarId}
          />
        </>
      )}
      {canViewColorCategory && (
        <EventSummaryColorCategory
          org={org}
          event={event}
          user={user}
          refetchEvent={refetchEvent}
        />
      )}
      {eventParent && (
        <AutopilotFormSaveOnEdit
          org={org}
          user={user}
          event={event}
          eventParent={eventParent}
          canAutopilot={!!canAutopilot}
          attendeeNotInDomain={!!attendeeNotInDomain}
          organizerNotClockwiseUser={!!organizerNotClockwiseUser}
          isAllDayEvent={!!isAllDayEvent || event?.isAllDay}
          calendarId={calendarId}
          enableOnMount={apEnableOnMount}
          setTimeFlexOnMount={
            apFlexWeekOnMount ? EFlex.Week : apFlexDayOnMount ? EFlex.Day : undefined
          }
          setRoomFlexOnMount={apRoomFlexOnMount}
          surface="Sidepanel"
        />
      )}
    </div>
  );
};
